import { Grid, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDrawer } from "../../../../components";
import { useBreakpoints, usePlanToCsv } from "../../../../hooks";
import { IPlan } from "../../../../models";
import { useGetPlanTree, useTeamStrategySettings } from "../hooks";
import { Blocks } from "./Blocks";
import { PlanHeader } from "./PlanHeader";
import { PlanVersionsDrawerContent } from "./PlanVersionsDrawerContent";

interface IProps {
  teamName: string;
  teamSlug: string;
  planGuid: string;
  headerContent: (args: {
    isLoading: boolean;
    isPastView: boolean;
    handleVersionsClicked: () => void;
    handleExportClicked: (filename: string) => void;
    plan?: IPlan;
    dateTimeLocal?: string | null;
    dateTimeLocalUtcOffsetInMinutes?: number | null;
  }) => React.ReactNode;
  canEdit?: boolean;
  meetingView?: boolean;
}

const Plan = (props: IProps) => {
  const [queryParams] = useSearchParams();
  const [dateTimeLocal, setDateTimeLocal] = useState<string>();
  const [dateTimeLocalUtcOffsetInMinutes, setDateTimeLocalUtcOffsetInMinutes] = useState<number>();
  const { plan, setPlan, isLoading, fetchActionItemSubitems } = useGetPlanTree(props.planGuid, dateTimeLocal);
  const { convertAndDownloadPlanAsCsv } = usePlanToCsv(props.planGuid);
  const [isTableView, setIsTableView] = useState(true);
  const [isPastView, setIsPastView] = useState(false);
  const container = React.useRef<HTMLDivElement>(null);
  const { showDrawer } = useDrawer();
  const { isMdUp } = useBreakpoints();
  const { settings, updateSettings } = useTeamStrategySettings(props.teamSlug);

  useEffect(() => {
    const date = queryParams.get("date");
    const offset = queryParams.get("offset");

    setDateTimeLocal(date ?? undefined);
    setDateTimeLocalUtcOffsetInMinutes(offset ? Number(offset) : undefined);
    setIsPastView(date != null && offset != null);
  }, [queryParams, plan]);

  useEffect(() => {
    handleViewToggled(settings.tableView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.teamSlug]);

  useEffect(() => {
    if (isMdUp) {
      setIsTableView(settings.tableView);
    } else {
      setIsTableView(false);
    }
  }, [isMdUp, props.teamSlug, settings]);

  function handleViewToggled(value: boolean) {
    setIsTableView(value);
    updateSettings({
      tableView: value,
    });
  }

  return (
    <Stack ref={container} spacing={{ xs: 0.75, md: 1 }}>
      <PlanHeader
        planGuid={props.planGuid}
        content={props.headerContent({
          isLoading: isLoading,
          isPastView: isPastView,
          handleVersionsClicked: () =>
            showDrawer({
              title: <Typography variant="h6">Version History</Typography>,
              content: <PlanVersionsDrawerContent planGuid={props.planGuid} planLastModified={plan?.lastModifiedUtc} />,
            }),
          handleExportClicked: (filename) => convertAndDownloadPlanAsCsv(filename),
          plan: plan,
          dateTimeLocal: dateTimeLocal,
          dateTimeLocalUtcOffsetInMinutes: dateTimeLocalUtcOffsetInMinutes,
        })}
        isTableView={isTableView}
        isPastView={isPastView}
        onViewToggled={handleViewToggled}
      />

      {!isLoading && plan !== undefined && (
        <Grid container sx={{ justifyContent: "center", width: "100%" }} spacing={{ xs: 0.75, md: 1 }}>
          <Blocks
            teamName={props.teamName}
            teamSlug={props.teamSlug}
            plan={plan}
            isTableView={isTableView}
            isPastView={isPastView}
            canEdit={props.canEdit}
            meetingView={props.meetingView}
            onActionItemSortOrderChanged={(updatedPlan) => setPlan(updatedPlan)}
            fetchActionItemSubitems={fetchActionItemSubitems}
          />
        </Grid>
      )}
    </Stack>
  );
};

export { Plan };
